import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Check, Clapperboard, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { fetchPostVideos, createPost, updatePost } from "@/lib/api";

/** Grid of Media videos (Posts table, Type=Media) with multi-select; reused by the editor's "Insert video". */
export function VideoPickerGrid({ userRecordId, selected, onToggle, single = false, inPost = [], onRemove = null }) {
  const [videos, setVideos] = useState(null);
  const [q, setQ] = useState("");
  const [kind, setKind] = useState("all");
  useEffect(() => {
    fetchPostVideos(userRecordId).then((r) => setVideos(r.items || [])).catch(() => setVideos([]));
  }, [userRecordId]);
  if (videos === null) return <div className="py-8 text-center text-sm text-zinc-500"><Loader2 className="inline animate-spin mr-2" size={14} />Loading videos…</div>;
  if (!videos.length) return <div className="py-8 text-center text-sm text-zinc-500 rounded-xl border border-dashed">No videos yet — make one in Marketing AI › Media.</div>;
  const used = new Set(inPost);
  const isUsed = (v) => used.has(v.post_rec_id) || (v.video_url && used.has(v.video_url));
  const needle = q.trim().toLowerCase();
  const isSlides = (v) => String(v.id || "").startsWith("sl-") || /^AI Slides/i.test(v.template_name || "");
  const shown = videos
    .filter((v) => kind === "all" || (kind === "slides" ? isSlides(v) : !isSlides(v)))
    .filter((v) => !needle || `${v.template_name || ""} ${v.business_name || ""}`.toLowerCase().includes(needle))
    .sort((a, b) => (a.business_name || "OreVital").localeCompare(b.business_name || "OreVital", undefined, { sensitivity: "base" }) || (a.template_name || "").localeCompare(b.template_name || ""));
  const usedCount = videos.filter(isUsed).length;
  const sameUrl = (v) => videos.filter((x) => x.video_url && x.video_url === v.video_url).map((x) => x.post_rec_id);
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search dealer or video…" className="h-8 rounded-lg text-xs" data-testid="video-picker-search" />
        <div className="flex rounded-lg border border-zinc-200 p-0.5 text-[11px] font-semibold shrink-0" data-testid="video-picker-kind">
          {[["all", `All ${videos.length}`], ["media", `Media ${videos.filter((v) => !isSlides(v)).length}`], ["slides", `Slides ${videos.filter(isSlides).length}`]].map(([k, label]) => (
            <button key={k} type="button" onClick={() => setKind(k)} className={`px-2 h-6 rounded-md ${kind === k ? "bg-zinc-900 text-white" : "text-zinc-600 hover:bg-zinc-100"}`} data-testid={`video-picker-kind-${k}`}>{label}</button>
          ))}
        </div>
        {usedCount > 0 && <span className="shrink-0 text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5" data-testid="video-picker-used-count">{usedCount} already in this post{onRemove ? " · click a badge to remove" : ""}</span>}
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-[46vh] overflow-y-auto pr-1" data-testid="video-picker-grid">
      {shown.map((v) => {
        const idx = selected.indexOf(v.post_rec_id);
        const on = idx >= 0;
        const already = isUsed(v);
        return (
          <button key={v.post_rec_id} type="button" onClick={() => onToggle(v)} data-testid={`video-pick-${v.id}`} title={already ? "Already in this post" : ""}
            className={`relative rounded-xl overflow-hidden border-2 text-left ${on ? "border-violet-600" : already ? "border-emerald-500/70" : "border-transparent hover:border-zinc-300"}`}>
            <div className={`aspect-[9/16] bg-zinc-900 ${already ? "opacity-60" : ""}`}>
              {v.thumbnail_url ? <img src={v.thumbnail_url} alt="" className="h-full w-full object-cover" loading="lazy" /> : <Clapperboard className="m-auto text-zinc-500 h-full" size={20} />}
            </div>
            <div className="p-1.5 bg-white">
              <div className="text-[11px] font-semibold text-zinc-800 truncate">{v.business_name || "OreVital"}</div>
              <div className="text-[10px] text-zinc-500 truncate">{isSlides(v) ? <span className="text-[#0b2545] font-semibold">Slides · </span> : null}{(v.template_name || "").replace(/^AI Slides · /, "")}</div>
            </div>
            {on && <span className="absolute top-1.5 left-1.5 h-5 min-w-5 px-1 rounded-full bg-violet-600 text-white text-[10px] font-bold flex items-center justify-center">{single ? <Check size={11} /> : idx + 1}</span>}
            {already && !on && (onRemove ? (
              <span role="button" tabIndex={0} title="Remove from this post" onClick={(e) => { e.stopPropagation(); onRemove(v, sameUrl(v)); }} onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); onRemove(v, sameUrl(v)); } }}
                className="absolute top-1.5 right-1.5 rounded-full bg-emerald-600 hover:bg-rose-600 text-white text-[10px] font-bold px-1.5 py-0.5 flex items-center gap-0.5" data-testid={`video-in-post-${v.id}`}><Check size={10} /> In post <X size={10} /></span>
            ) : (
              <span className="absolute top-1.5 right-1.5 rounded-full bg-emerald-600 text-white text-[10px] font-bold px-1.5 py-0.5 flex items-center gap-0.5" data-testid={`video-in-post-${v.id}`}><Check size={10} /> In post</span>
            ))}
          </button>
        );
      })}
      {!shown.length && <div className="col-span-full py-6 text-center text-xs text-zinc-500">No videos match “{q}”</div>}
      </div>
    </div>
  );
}

// Build (or re-edit) a "video showcase" post: pick 1..many Media videos → grid-of-cards post.
export default function VideoShowcaseDialog({ userRecordId, post, isAdmin = false, onClose, onSaved }) {
  const [title, setTitle] = useState(post?.title || "Our videos");
  const [intro, setIntro] = useState(post?.caption || "");
  const [picked, setPicked] = useState(post?.video_ids || []);
  const [shared, setShared] = useState(post ? !!post.shared : false);
  const [cols, setCols] = useState(post?.grid?.cols || 3);
  const [shape, setShape] = useState(post?.grid?.shape || "portrait");
  const [saving, setSaving] = useState(false);

  function toggle(v) {
    setPicked((arr) => (arr.includes(v.post_rec_id) ? arr.filter((x) => x !== v.post_rec_id) : [...arr, v.post_rec_id]));
  }
  async function save() {
    if (!title.trim()) { toast.error("Give the showcase a title"); return; }
    if (!picked.length) { toast.error("Pick at least one video"); return; }
    setSaving(true);
    try {
      const body = { user_record_id: userRecordId, title: title.trim(), caption: intro.trim(), video_ids: picked, shared, grid: { cols, shape } };
      const r = post ? await updatePost(post.id, body) : await createPost(body);
      toast.success(post ? "Showcase updated" : "Video showcase created — add it to your home tabs in Settings");
      onSaved(r.post);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  }

  return (
    <section className="rounded-2xl border border-violet-200 bg-white p-4 space-y-3" data-testid="video-showcase-dialog">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold text-zinc-900">{post ? "Edit video showcase" : "New video showcase"}</h3>
        <button type="button" onClick={onClose} className="text-xs font-semibold text-zinc-500 hover:text-zinc-800" data-testid="video-showcase-dialog-back">← Back to posts</button>
      </div>
      <div>
        <div className="space-y-3">
          <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Tab title, e.g. Dealer videos" className="rounded-xl" data-testid="showcase-title" />
          <Textarea value={intro} onChange={(e) => setIntro(e.target.value)} rows={2} placeholder="Optional intro line shown above the grid" className="rounded-xl text-sm" data-testid="showcase-intro" />
          <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="showcase-layout">
            <span className="font-semibold text-zinc-700">Layout:</span>
            {[2, 3, 4].map((n) => (
              <button key={n} type="button" onClick={() => setCols(n)} data-testid={`showcase-cols-${n}`}
                className={`h-8 px-3 rounded-full font-semibold ${cols === n ? "bg-zinc-900 text-white" : "bg-white border border-zinc-200 text-zinc-600"}`}>{n} per row</button>
            ))}
            <span className="w-px h-5 bg-zinc-200 mx-1" />
            {[["portrait", "Portrait"], ["square", "Square"]].map(([v, l]) => (
              <button key={v} type="button" onClick={() => setShape(v)} data-testid={`showcase-shape-${v}`}
                className={`h-8 px-3 rounded-full font-semibold ${shape === v ? "bg-zinc-900 text-white" : "bg-white border border-zinc-200 text-zinc-600"}`}>{l}</button>
            ))}
          </div>
          <div className="text-xs text-zinc-500">Tap videos in the order you want them shown · {picked.length} selected</div>
          <VideoPickerGrid userRecordId={userRecordId} selected={picked} onToggle={toggle} />
          {!isAdmin && (
            <label className="flex items-center gap-2 text-xs text-zinc-700">
              <input type="checkbox" checked={shared} onChange={(e) => setShared(e.target.checked)} data-testid="showcase-shared" /> Share with other dealers
            </label>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={onClose} className="rounded-xl">Cancel</Button>
            <Button onClick={save} disabled={saving} className="rounded-xl bg-violet-600 hover:bg-violet-700 text-white" data-testid="showcase-save">
              {saving ? <Loader2 className="animate-spin mr-1.5" size={14} /> : <Check size={14} className="mr-1.5" />} {post ? "Save" : "Create showcase"}
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
